import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { map } from 'rxjs';
import { UserService } from './user.service';
import { enUS, Locale, ptBR } from 'date-fns/locale';

interface ILanguageOptions {
  en: string;
  pt: string;
}

export enum LanguageCode {
  EN = 'en',
  PT = 'pt',
}

interface IFormatedLanguageOption {
  label: string;
  code: LanguageCode;
}

export const DEFAULT_LANGUAGE = LanguageCode.EN;
const LOCALSTORAGE_LANGUAGE_KEY = 'language';

export function currentOrDefaultLanguage(): LanguageCode {
  const localStorageLanguage = localStorage.getItem(LOCALSTORAGE_LANGUAGE_KEY) as LanguageCode | null;

  return localStorageLanguage || DEFAULT_LANGUAGE;
}

export function storedLanguage(): LanguageCode | null {
  const localStorageLanguage = localStorage.getItem(LOCALSTORAGE_LANGUAGE_KEY) as LanguageCode | null;

  return localStorageLanguage;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  translateService = inject(TranslateService);
  primeNgConfig = inject(PrimeNGConfig);
  userService = inject(UserService);

  private readonly BROWSER_LANGUAGE = this.translateService.getBrowserLang() as LanguageCode | undefined;

  languageOptions$ = this.translateService.stream('top_menu.user_configurations.languages.options').pipe(
    map((options: ILanguageOptions) => {
      const languages: IFormatedLanguageOption[] = [];

      Object.entries(options).forEach(([key, translatedLabel]) => {
        languages.push({
          label: translatedLabel,
          code: key as LanguageCode,
        });
      });

      return languages;
    })
  );

  getCurrentLanguage = () => {
    return currentOrDefaultLanguage();
  };

  setDefaultLanguage(): void {
    this._setPrimeNgTranslations();

    if (this.storedLanguage) {
      this._useLanguage(this.storedLanguage);
    } else if (this.BROWSER_LANGUAGE) {
      this._useLanguage(this.BROWSER_LANGUAGE);
    } else {
      this._useLanguage(DEFAULT_LANGUAGE);
    }
  }

  updateUserLanguagePreference(language: LanguageCode): void {
    this._useLanguage(language);
    this.userService.updateLanguagePreference(language).subscribe();
  }

  getLocaleForDateFns(): Locale {
    const currentLang = this.translateService.currentLang;

    return currentLang === 'pt' ? ptBR : enUS;
  }

  private storedLanguage = storedLanguage();

  private _useLanguage(language: LanguageCode): void {
    const currentLanguage = this.getCurrentLanguage();

    if (currentLanguage !== language) localStorage.setItem(LOCALSTORAGE_LANGUAGE_KEY, language);

    this.translateService.use(language);
  }

  private _setPrimeNgTranslations() {
    this.translateService.get('primeng').subscribe((res) => this.primeNgConfig.setTranslation(res));
  }
}
